/**
 * RouteErrorBoundary — auto-resetting error boundary for SPA routes.
 *
 * Background (Night 8 RCA):
 *
 *   The existing `AppErrorBoundary` is a class component holding
 *   `hasError` in state. When wrapped INSIDE `AppLayout`, that layout
 *   persists across soft-navigations (React Router v6 reconciles the
 *   same JSX tree). The boundary instance therefore persists too.
 *
 *   Consequence: if any render error fires on route X, the boundary
 *   catches it and shows the "Something went wrong" fallback. On the
 *   user's next click in the left nav, soft-nav succeeds, the URL
 *   updates, BUT the boundary is still in `hasError === true` state.
 *   Every route thereafter renders the fallback. Only `window.location.
 *   reload()` (hard reload) clears it.
 *
 *   This matches the prod report "breaks on soft-nav, works on hard
 *   reload" — independent of whether the underlying render error is
 *   the one Nikos hit or a future one.
 *
 * Fix: wrap `<AppErrorBoundary>` in a component that reads
 * `useLocation()` and passes `key={location.pathname}` to it. React
 * unmounts the old boundary and mounts a fresh one when the key
 * changes, so `hasError` starts at `false` on every new route.
 *
 * Single-responsibility: this component knows about routing; the
 * underlying boundary does not. The boundary stays a generic
 * class-based error handler usable in non-routing contexts (root
 * `<App>`, modals, etc.).
 */

import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AppErrorBoundary } from './AppErrorBoundary';

interface Props {
  children: ReactNode;
}

export function RouteErrorBoundary({ children }: Props): JSX.Element {
  const location = useLocation();
  return (
    <AppErrorBoundary key={location.pathname}>
      {children}
    </AppErrorBoundary>
  );
}
